import { Box, LinearProgress, Stack, Tooltip, Typography } from "@mui/material";
import { Control, useWatch } from "react-hook-form";

import type { ManifestFormValues } from "../types";

import { manifestFillProgress } from "../hooks/useManifestFillProgress";

/**
 * How much of the manifest is filled in.
 *
 * "Filled", not "confirmed": there is no per-field confirm on this form, so a
 * confirmed count would either sit at zero for a clean extraction nobody needed
 * to touch, or count edits and call them something they are not. On a poor scan
 * most of these fields start empty, and working the number up is the job.
 *
 * Subscribed here rather than in the dialog, and through `compute` rather than a
 * plain watch. The count has to move as the reviewer types, which means something must listen
 * to every field. Listening from the dialog re-rendered the whole form and the
 * PDF pane on every keystroke to move a progress bar. Listening here confines
 * that to the bar, and `compute` narrows it further: react-hook-form compares
 * the computed value and only re-renders when the count itself changes, so most
 * keystrokes — every character after the first of a field — cost nothing.
 */
export function ManifestFillProgress({ control }: { control: Control<ManifestFormValues> }) {
  const progress = useWatch({ control, compute: manifestFillProgress });

  if (progress.total === 0) return null;

  const percent = Math.round((progress.filled / progress.total) * 100);
  const complete = progress.filled === progress.total;

  return (
    <Tooltip title="Fields a complete manifest carries. Optional boxes — discrepancy narratives, second address lines, the Texas registration number — are not counted, so this can reach complete.">
      <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 160 }}>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
          {progress.filled} of {progress.total} fields filled
        </Typography>
        <Box sx={{ flex: 1, minWidth: 48 }}>
          <LinearProgress
            variant="determinate"
            value={percent}
            color={complete ? "success" : "primary"}
            // Named for a screen reader, which cannot see the caption beside it.
            aria-label={`${progress.filled} of ${progress.total} manifest fields filled`}
            sx={{ height: 6, borderRadius: 3 }}
          />
        </Box>
      </Stack>
    </Tooltip>
  );
}
